import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setSidebarCollapsed } from '../actions/sidebar';
import media from '../util/media';

const MEASURE = '78ch';
const HEADING_COLOR = '#111';
const MUTED = '#6b7280';
const BORDER = 'rgba(0,0,0,0.08)';

const OuterContainer = styled.div`
  display: block;
  width: 100%;
`;

const Main = styled.main`
  max-width: ${MEASURE};
  margin: 0 auto;
  padding: 3rem 1.25rem 4rem;
  font-size: 18px;
  line-height: 1.6;
  padding-top: 150px;
`;

const Headline = styled.h1`
  margin: 0 0 0.5rem 0;
  color: ${HEADING_COLOR};
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${MUTED};
  margin: 0 0 0.25rem 0;
`;

const DutchNote = styled.p`
  font-size: 14px;
  color: ${MUTED};
  font-style: italic;
  margin: 0 0 2.5rem 0;
`;

const Article = styled.section`
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${BORDER};

  &.first {
    border-top: none;
    margin-top: 1.5rem;
    padding-top: 0;
  }
`;

const ArticleHeading = styled.h2`
  margin: 0 0 0.75rem 0;
  color: ${HEADING_COLOR};
  font-size: 22px;
`;

const ClauseList = styled.ol`
  margin: 0;
  padding: 0 0 0 1.5rem;
  list-style: decimal outside;
  counter-reset: none;
`;

const Clause = styled.li`
  margin-bottom: 0.65rem;
  font-size: 16.5px;
  line-height: 1.6;
`;

const SubList = styled.ol`
  margin: 0.35rem 0 0.35rem 1.25rem;
  padding: 0;
  list-style: lower-alpha outside;
`;

const SubItem = styled.li`
  margin-bottom: 0.3rem;
  font-size: 16px;
`;

const Ital = styled.span`
  font-style: italic;
  color: ${MUTED};
`;

const EndMark = styled.p`
  text-align: center;
  color: ${MUTED};
  font-style: italic;
  font-size: 15px;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${BORDER};
`;

const ResponsiveWrapper = styled.div`
  ${media.phone`
    ${Main} {
      padding-top: 100px;
    }
    ${Headline} {
      font-size: 34px;
    }
  `}
`;

class Terms extends React.PureComponent {
  componentDidMount() {
    this.props.dispatch(setSidebarCollapsed(true));
  }

  render() {
    return (
      <OuterContainer>
        <ResponsiveWrapper>
          <Main className="main-content">
            <Headline>General Terms and Conditions</Headline>
            <Subtitle>Code R — Software Development &amp; Consultancy Services</Subtitle>
            <DutchNote>
              Where applicable, the corresponding Dutch legal terms are provided in
              italicised parentheses for reference under Dutch law.
            </DutchNote>

            {/* Article 1 – Definitions */}
            <Article className="first">
              <ArticleHeading>Article 1 – Definitions</ArticleHeading>
              <p style={{ marginBottom: '0.5rem', fontSize: '16.5px' }}>
                In these General Terms and Conditions, the following terms have the meanings
                set out below:
              </p>
              <ClauseList>
                <Clause>
                  <strong>Service Provider</strong>: Code R, registered with the Dutch Chamber of Commerce
                  (KvK) under number 96461039, providing software development and/or
                  consultancy services under these Terms.
                </Clause>
                <Clause>
                  <strong>Client</strong>: the natural or legal person who engages the Service Provider.
                </Clause>
                <Clause>
                  <strong>Agreement</strong>: any agreement between the Service Provider and the Client,
                  including amendments and all acts performed in preparation and execution
                  thereof.
                </Clause>
                <Clause>
                  <strong>Services</strong>: all software development, consultancy, advisory, and related
                  services provided by the Service Provider.
                </Clause>
                <Clause>
                  <strong>Deliverables</strong>: all items, code, documentation, reports, designs, or other
                  materials produced by the Service Provider under the Agreement.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 2 – Applicability */}
            <Article>
              <ArticleHeading>Article 2 – Applicability</ArticleHeading>
              <ClauseList>
                <Clause>
                  These Terms apply to all offers, quotations, agreements, and services
                  provided by the Service Provider.
                </Clause>
                <Clause>
                  Deviations from these Terms are only binding if expressly agreed in writing.
                </Clause>
                <Clause>
                  The Client's own general terms and conditions are expressly rejected, unless
                  agreed otherwise in writing.
                </Clause>
                <Clause>
                  If any provision of these Terms is held to be invalid or unenforceable, the
                  remaining provisions shall remain in full force and effect.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 3 – Quotations and Formation of Agreement */}
            <Article>
              <ArticleHeading>Article 3 – Quotations and Formation of Agreement</ArticleHeading>
              <ClauseList>
                <Clause>
                  All quotations and offers are without obligation unless expressly stated
                  otherwise.
                </Clause>
                <Clause>
                  An Agreement is formed upon written acceptance of a quotation by the Client,
                  or when the Service Provider commences performance of the Services with the
                  Client's knowledge.
                </Clause>
                <Clause>
                  Any subsequent amendments to the Agreement must be agreed in writing by both
                  parties.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 4 – Performance of Services */}
            <Article>
              <ArticleHeading>Article 4 – Performance of Services</ArticleHeading>
              <ClauseList>
                <Clause>
                  The Service Provider shall perform the Services to the best of its abilities
                  and in accordance with the standards of good workmanship
                  (<Ital>goed opdrachtnemerschap</Ital>).
                </Clause>
                <Clause>
                  Unless explicitly agreed otherwise, the Service Provider's obligations are
                  best-efforts obligations (<Ital>inspanningsverbintenis</Ital>) and not
                  obligations to achieve a specific result
                  (<Ital>resultaatsverbintenis</Ital>).
                </Clause>
                <Clause>
                  The Client shall provide all information, access, and cooperation reasonably
                  necessary for the proper performance of the Services in a timely manner.
                  Delays caused by the Client may result in adjusted timelines and additional
                  costs.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 5 – Timelines and Delivery */}
            <Article>
              <ArticleHeading>Article 5 – Timelines and Delivery</ArticleHeading>
              <ClauseList>
                <Clause>
                  All delivery dates and timelines are indicative unless expressly agreed as
                  firm deadlines (<Ital>fatale termijnen</Ital>) in writing.
                </Clause>
                <Clause>
                  Exceeding an indicative timeline does not entitle the Client to damages,
                  termination, or suspension of any obligation under the Agreement.
                </Clause>
                <Clause>
                  If a firm deadline is at risk of being exceeded, the parties shall consult in
                  good faith regarding an adjusted schedule.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 6 – Fees and Payment */}
            <Article>
              <ArticleHeading>Article 6 – Fees and Payment</ArticleHeading>
              <ClauseList>
                <Clause>
                  All fees are in euros and exclusive of VAT and other government levies,
                  unless stated otherwise.
                </Clause>
                <Clause>
                  Unless agreed otherwise, the Service Provider invoices monthly in arrears for
                  time-and-materials engagements, or according to the milestones specified in
                  the Agreement.
                </Clause>
                <Clause>
                  Payment is due within 30 days of the invoice date, unless otherwise agreed in
                  writing.
                </Clause>
                <Clause>
                  In the event of late payment, the Client is in default by operation of law.
                  The Service Provider is entitled to charge statutory commercial interest
                  (<Ital>wettelijke handelsrente</Ital> pursuant to Article 6:119a of the Dutch
                  Civil Code) from the due date. For Clients outside the Netherlands, interest
                  shall accrue at a rate of 1.5% per month or the maximum rate permitted by
                  applicable law, whichever is lower.
                </Clause>
                <Clause>
                  All reasonable extrajudicial collection costs incurred by the Service Provider
                  are for the Client's account.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 7 – Intellectual Property Rights */}
            <Article>
              <ArticleHeading>Article 7 – Intellectual Property Rights</ArticleHeading>
              <ClauseList>
                <Clause>
                  Upon full payment of all amounts due under the Agreement, all intellectual
                  property rights to the Deliverables created specifically for the Client under
                  the Agreement shall transfer to the Client. Until full payment, all
                  intellectual property rights remain with the Service Provider.
                </Clause>
                <Clause>
                  The transfer of intellectual property rights does not extend to pre-existing
                  materials, frameworks, libraries, tools, or components owned by or licensed to
                  the Service Provider prior to or independently of the Agreement ("Pre-Existing
                  IP"). The Client receives a perpetual, non-exclusive, royalty-free licence to
                  use any Pre-Existing IP incorporated in the Deliverables, solely as part of
                  those Deliverables.
                </Clause>
                <Clause>
                  The Service Provider retains the right to use general knowledge, experience,
                  techniques, and ideas gained during the performance of the Services for other
                  purposes, provided this does not involve disclosure of the Client's
                  confidential information.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 8 – Confidentiality */}
            <Article>
              <ArticleHeading>Article 8 – Confidentiality</ArticleHeading>
              <ClauseList>
                <Clause>
                  Both parties shall treat all confidential information received from the other
                  party as strictly confidential, both during and after the term of the
                  Agreement.
                </Clause>
                <Clause>
                  Confidential information means all information that is designated as
                  confidential or that should reasonably be understood to be confidential given
                  its nature.
                </Clause>
                <Clause>
                  This obligation does not apply to information that:
                  <SubList>
                    <SubItem>was already public knowledge;</SubItem>
                    <SubItem>was independently developed;</SubItem>
                    <SubItem>was received from a third party without restriction; or</SubItem>
                    <SubItem>must be disclosed by law or regulation.</SubItem>
                  </SubList>
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 9 – Liability */}
            <Article>
              <ArticleHeading>Article 9 – Liability</ArticleHeading>
              <ClauseList>
                <Clause>
                  The Service Provider's total aggregate liability under the Agreement is
                  limited to the amount of fees actually paid by the Client under the Agreement
                  in the six (6) months preceding the event giving rise to the liability, up to
                  a maximum of €50,000 (fifty thousand euros).
                </Clause>
                <Clause>
                  The Service Provider shall not be liable for indirect damages, including but
                  not limited to consequential damages, loss of profit, loss of data, loss of
                  goodwill, business interruption, or damage to third parties.
                </Clause>
                <Clause>
                  The limitations of liability in this article do not apply in cases of
                  intentional misconduct (<Ital>opzet</Ital>) or gross negligence
                  (<Ital>grove schuld</Ital>) on the part of the Service Provider.
                </Clause>
                <Clause>
                  Any claim for damages must be filed in writing within twenty-four (24) months after
                  the Client became aware or should reasonably have become aware of the damage.
                  Claims not filed within this period are forfeited.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 10 – Force Majeure */}
            <Article>
              <ArticleHeading>Article 10 – Force Majeure</ArticleHeading>
              <ClauseList>
                <Clause>
                  Neither party shall be liable for any failure or delay in performing its
                  obligations where such failure or delay results from circumstances beyond its
                  reasonable control (force majeure / <Ital>overmacht</Ital>).
                </Clause>
                <Clause>
                  Force majeure includes, but is not limited to: internet or infrastructure
                  failures, power outages, government measures, pandemics, natural disasters,
                  and failures of third-party suppliers.
                </Clause>
                <Clause>
                  If force majeure persists for more than sixty (60) days, either party may
                  terminate the Agreement in writing without liability, subject to payment for
                  Services already performed.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 11 – Term and Termination */}
            <Article>
              <ArticleHeading>Article 11 – Term and Termination</ArticleHeading>
              <ClauseList>
                <Clause>
                  The Agreement is entered into for the period specified therein, or, if no
                  period is specified, for an indefinite period.
                </Clause>
                <Clause>
                  Either party may terminate an indefinite-term Agreement by giving at least
                  thirty (30) days' prior written notice.
                </Clause>
                <Clause>
                  Either party may terminate the Agreement with immediate effect by written
                  notice if the other party:
                  <SubList>
                    <SubItem>
                      is in material breach and fails to cure within fourteen (14) days of
                      written notice;
                    </SubItem>
                    <SubItem>
                      is declared bankrupt, files for suspension of payment, or ceases
                      operations.
                    </SubItem>
                  </SubList>
                </Clause>
                <Clause>
                  Upon termination, the Client shall pay for all Services performed and expenses
                  incurred up to the effective date of termination.
                </Clause>
                <Clause>
                  Provisions that by their nature should survive termination (including but not
                  limited to confidentiality, intellectual property, and liability) shall remain
                  in effect.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 12 – Data Protection */}
            <Article>
              <ArticleHeading>Article 12 – Data Protection</ArticleHeading>
              <ClauseList>
                <Clause>
                  To the extent that the Service Provider processes personal data on behalf of
                  the Client, the Service Provider acts as a processor within the meaning of the
                  General Data Protection Regulation (GDPR). The parties shall enter into a
                  separate data processing agreement where required.
                </Clause>
                <Clause>
                  The Service Provider shall implement appropriate technical and organisational
                  measures to protect personal data against loss or unlawful processing.
                </Clause>
              </ClauseList>
            </Article>

            {/* Article 13 – Applicable Law and Dispute Resolution */}
            <Article>
              <ArticleHeading>Article 13 – Applicable Law and Dispute Resolution</ArticleHeading>
              <ClauseList>
                <Clause>
                  These Terms and all Agreements are governed by the laws of the Netherlands.
                </Clause>
                <Clause>
                  The parties shall first attempt to resolve any dispute through good-faith
                  negotiation.
                </Clause>
                <Clause>
                  If a dispute cannot be resolved amicably within thirty (30) days, it shall be
                  submitted to the competent court in the district where the Service Provider
                  has its registered office.
                </Clause>
                <Clause>
                  The United Nations Convention on Contracts for the International Sale of Goods
                  (CISG) is expressly excluded.
                </Clause>
              </ClauseList>
            </Article>

            <EndMark>– End of General Terms and Conditions –</EndMark>
          </Main>
        </ResponsiveWrapper>
      </OuterContainer>
    );
  }
}

export default connect()(Terms);
